import type { Perfil } from '../../core/services/autenticacion.service';

/**
 * IDs de perfiles definidos por negocio.
 * Ver docs/HEADER_SELECTORES.md
 */
export const IDS_PERFIL = {
  EXTENSIONISTA: 1,
  EJECUTIVO_INTEGRAL: 2,
  JEFE_DE_AREA: 3,
  ENCARGADO_REGIONAL: 4,
  DIRECTOR_REGIONAL: 5,
  NIVEL_CENTRAL: 6,
} as const;

/** Solo perfil 6: opción sintética "Todas las Regiones" en el selector (id_jerarquia positivo). */
export const ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL = 1;

export type IdPerfil = typeof IDS_PERFIL[keyof typeof IDS_PERFIL];
export type PerfilMinimo = Pick<Perfil, 'id_perfil'> | null | undefined;

export type ReglasHeader = {
  mostrarSelectorRegion: boolean;
  mostrarSelectorArea: boolean;
  mostrarSelectorUO: boolean;
  opcionTodasLasRegionesNivelCentral: boolean;
};

/**
 * Qué selectores muestra el header según id_perfil.
 */
export function obtenerReglasHeader(perfil: PerfilMinimo): ReglasHeader {
  const id = perfil?.id_perfil;
  if (id === IDS_PERFIL.EXTENSIONISTA) {
    return {
      mostrarSelectorRegion: false,
      mostrarSelectorArea: false,
      mostrarSelectorUO: true,
      opcionTodasLasRegionesNivelCentral: false,
    };
  }
  if (id === IDS_PERFIL.EJECUTIVO_INTEGRAL || id === IDS_PERFIL.JEFE_DE_AREA) {
    return {
      mostrarSelectorRegion: false,
      mostrarSelectorArea: true,
      mostrarSelectorUO: true,
      opcionTodasLasRegionesNivelCentral: false,
    };
  }
  if (id === IDS_PERFIL.ENCARGADO_REGIONAL || id === IDS_PERFIL.DIRECTOR_REGIONAL) {
    return {
      mostrarSelectorRegion: true,
      mostrarSelectorArea: true,
      mostrarSelectorUO: true,
      opcionTodasLasRegionesNivelCentral: false,
    };
  }
  if (id === IDS_PERFIL.NIVEL_CENTRAL) {
    return {
      mostrarSelectorRegion: true,
      mostrarSelectorArea: true,
      mostrarSelectorUO: true,
      opcionTodasLasRegionesNivelCentral: true,
    };
  }
  return {
    mostrarSelectorRegion: false,
    mostrarSelectorArea: true,
    mostrarSelectorUO: true,
    opcionTodasLasRegionesNivelCentral: false,
  };
}

/**
 * Valida si el perfil corresponde a un ID objetivo.
 */
export function esPerfil(perfil: PerfilMinimo, idPerfil: IdPerfil): boolean {
  return perfil?.id_perfil === idPerfil;
}

/**
 * id_jerarquia para endpoints .../perfil/{id}/jerarquia/{id}/...
 * Ver docs/HEADER_SELECTORES.md sección 5.
 */
export function calcularIdJerarquiaParaEndpoint(
  perfil: PerfilMinimo,
  idRegion: number,
  idArea: number,
  idUO: number
): number {
  if (idUO !== -1) {
    return idUO;
  }
  const idp = perfil?.id_perfil;
  if (idp === IDS_PERFIL.EXTENSIONISTA) {
    return -1;
  }
  if (idp === IDS_PERFIL.EJECUTIVO_INTEGRAL || idp === IDS_PERFIL.JEFE_DE_AREA) {
    if (idArea === -1) {
      return -1;
    }
    return idArea;
  }
  if (idp === IDS_PERFIL.ENCARGADO_REGIONAL || idp === IDS_PERFIL.DIRECTOR_REGIONAL) {
    if (idArea !== -1) {
      return idArea;
    }
    return idRegion;
  }
  if (idp === IDS_PERFIL.NIVEL_CENTRAL) {
    if (idArea !== -1) {
      return idArea;
    }
    if (idRegion === ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL) {
      return ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL;
    }
    return idRegion;
  }
  if (idArea !== -1) {
    return idArea;
  }
  return idRegion !== -1 ? idRegion : -1;
}

/**
 * id_jerarquia para GET .../obtenerRegiones/{id_perfil}/jerarquia/{id_jerarquia}
 */
export function calcularIdJerarquiaParaCargaRegiones(_perfil: PerfilMinimo): number {
  return -1;
}

/**
 * id_jerarquia padre para GET .../obtenerAreas/{id_perfil}/jerarquia/{id_jerarquia}
 */
export function calcularIdJerarquiaParaCargaAreas(
  perfil: PerfilMinimo,
  idRegion: number
): number {
  const idp = perfil?.id_perfil;
  if (idp === IDS_PERFIL.EXTENSIONISTA) {
    return -1;
  }
  if (idp === IDS_PERFIL.EJECUTIVO_INTEGRAL || idp === IDS_PERFIL.JEFE_DE_AREA) {
    return -1;
  }
  if (idp === IDS_PERFIL.ENCARGADO_REGIONAL || idp === IDS_PERFIL.DIRECTOR_REGIONAL) {
    return idRegion !== -1 ? idRegion : -1;
  }
  if (idp === IDS_PERFIL.NIVEL_CENTRAL) {
    if (idRegion === ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL) {
      return ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL;
    }
    return idRegion !== -1 ? idRegion : -1;
  }
  return -1;
}

/**
 * id_jerarquia padre para GET .../obtenerUnidadesOperativas/{id_perfil}/jerarquia/{id_jerarquia}
 */
export function calcularIdJerarquiaParaCargaUO(
  perfil: PerfilMinimo,
  idRegion: number,
  idAreaSeleccionada: number
): number {
  const idp = perfil?.id_perfil;
  if (idp === IDS_PERFIL.EXTENSIONISTA) {
    return -1;
  }
  if (idp === IDS_PERFIL.EJECUTIVO_INTEGRAL || idp === IDS_PERFIL.JEFE_DE_AREA) {
    return idAreaSeleccionada;
  }
  if (idAreaSeleccionada !== -1) {
    return idAreaSeleccionada;
  }
  if (idRegion !== -1) {
    return idRegion;
  }
  return -1;
}

export function esPerfilExtensionista(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.EXTENSIONISTA);
}

/**
 * Pestañas Plan de Negocio en /gestión (diagnóstico, soluciones, plan de trabajo, etc.):
 * solo el perfil Extensionista puede editar; los demás son solo lectura.
 */
export function puedeEditarPlanNegocioGestion(perfil: PerfilMinimo): boolean {
  return esPerfilExtensionista(perfil);
}

export function esPerfilEjecutivoIntegral(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.EJECUTIVO_INTEGRAL);
}

/** Gestión → Encuesta de Supervisión: solo Ejecutivo Integral. */
export function puedeVerEncuestaSupervisionGestion(perfil: PerfilMinimo): boolean {
  return esPerfilEjecutivoIntegral(perfil);
}

export function esPerfilJefeDeArea(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.JEFE_DE_AREA);
}

export function esPerfilEncargadoRegional(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.ENCARGADO_REGIONAL);
}

export function esPerfilDirectorRegional(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.DIRECTOR_REGIONAL);
}

export function esPerfilNivelCentral(perfil: PerfilMinimo): boolean {
  return esPerfil(perfil, IDS_PERFIL.NIVEL_CENTRAL);
}

/**
 * Tarjeta "Solicitudes Pendientes" del dashboard: solo ejecutivo integral y encargado regional.
 */
export function puedeVerTarjetaSolicitudesPendientes(perfil: PerfilMinimo): boolean {
  const id = perfil?.id_perfil;
  return id === IDS_PERFIL.EJECUTIVO_INTEGRAL || id === IDS_PERFIL.ENCARGADO_REGIONAL;
}

/**
 * Gestión plan de negocio: pueden aprobar/rechazar solicitudes y editar comentarios por TAB.
 */
export function puedeAprobarPlanNegocioGestion(perfil: PerfilMinimo): boolean {
  return puedeVerTarjetaSolicitudesPendientes(perfil);
}
